import { useState } from "react";
import {
  createCustomEnvironment,
  deleteCustomEnvironment as deleteStoredEnvironment,
  isProductionEnvironment,
  updateCustomEnvironment,
  validateEnvironmentUrl
} from "./environmentConfig";

export default function EnvironmentPage({
  activeEnvironment,
  showEnvironmentUi,
  environments,
  onEnvironmentSelected,
  onEnvironmentDefinitionsChanged,
  onNavigateBack,
  backLabel = "Back"
}) {
  const showEnvironmentBadge =
    Boolean(activeEnvironment?.label) && !isProductionEnvironment(activeEnvironment);
  const [environmentForm, setEnvironmentForm] = useState({ label: "", base_url: "" });
  const [editingEnvironmentKey, setEditingEnvironmentKey] = useState("");
  const [environmentError, setEnvironmentError] = useState("");

  function startEnvironmentEdit(environment) {
    if (environment.readonly) return;
    setEditingEnvironmentKey(environment.key);
    setEnvironmentForm({
      label: environment.label,
      base_url: environment.baseUrl
    });
    setEnvironmentError("");
  }

  function resetEnvironmentForm() {
    setEditingEnvironmentKey("");
    setEnvironmentForm({ label: "", base_url: "" });
    setEnvironmentError("");
  }

  function saveEnvironment() {
    const trimmedLabel = environmentForm.label.trim();
    const validationMessage = validateEnvironmentUrl(environmentForm.base_url);
    if (!trimmedLabel) {
      setEnvironmentError("Environment name is required.");
      return;
    }
    if (validationMessage) {
      setEnvironmentError(validationMessage);
      return;
    }

    if (editingEnvironmentKey) {
      updateCustomEnvironment(editingEnvironmentKey, {
        label: trimmedLabel,
        baseUrl: environmentForm.base_url
      });
    } else {
      createCustomEnvironment({
        label: trimmedLabel,
        baseUrl: environmentForm.base_url
      });
    }

    resetEnvironmentForm();
    onEnvironmentDefinitionsChanged?.();
  }

  function removeEnvironment(environment) {
    if (environment.readonly || activeEnvironment?.key === environment.key) return;
    const confirmed = window.confirm(`Delete environment ${environment.label}?`);
    if (!confirmed) return;
    deleteStoredEnvironment(environment.key);
    if (editingEnvironmentKey === environment.key) {
      resetEnvironmentForm();
    }
    onEnvironmentDefinitionsChanged?.();
  }

  function selectEnvironment(environmentKey) {
    if (activeEnvironment?.key === environmentKey) {
      return;
    }
    onEnvironmentSelected?.(environmentKey);
  }

  return (
    <main className="auth-shell">
      <section className="auth-card environment-page-card">
        <div className="auth-environment-row">
          <div className="route-title">
            <strong>Environment Settings</strong>
            <small>Select where this browser should connect.</small>
          </div>
          {showEnvironmentBadge ? (
            <span className="environment-badge">{activeEnvironment.label}</span>
          ) : null}
        </div>

        {!showEnvironmentUi ? (
          <>
            <p className="auth-helper">
              Environment switching is not available on this host.
            </p>
            <div className="environment-page-actions">
              <button type="button" className="btn-game-secondary" onClick={onNavigateBack}>
                {backLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="manage-environment-form-grid">
              <label>
                Environment Name
                <input
                  value={environmentForm.label}
                  placeholder="Development"
                  onChange={(event) =>
                    setEnvironmentForm((previous) => ({ ...previous, label: event.target.value }))
                  }
                />
              </label>
              <label>
                Environment URL
                <input
                  value={environmentForm.base_url}
                  placeholder="https://your-api-host.example.com"
                  onChange={(event) =>
                    setEnvironmentForm((previous) => ({
                      ...previous,
                      base_url: event.target.value
                    }))
                  }
                />
              </label>
            </div>

            {environmentError ? <p className="form-error">{environmentError}</p> : null}

            <div className="manage-environment-form-actions">
              <button type="button" className="btn-game-primary" onClick={saveEnvironment}>
                {editingEnvironmentKey ? "Save Environment" : "Add Environment"}
              </button>
              <button
                type="button"
                className="btn-game-secondary"
                onClick={resetEnvironmentForm}
                disabled={!editingEnvironmentKey && !environmentForm.label && !environmentForm.base_url}
              >
                Clear
              </button>
              <button type="button" className="btn-game-secondary" onClick={onNavigateBack}>
                {backLabel}
              </button>
            </div>

            <div className="manage-environment-list">
              {environments.map((environment) => {
                const isActiveEnvironment = activeEnvironment?.key === environment.key;
                return (
                  <article key={environment.key} className="manage-environment-item">
                    <div className="manage-environment-copy">
                      <strong>{environment.label}</strong>
                      <small>{environment.baseUrl}</small>
                    </div>
                    <div className="manage-environment-actions">
                      <button
                        type="button"
                        className={isActiveEnvironment ? "btn-game-primary" : "btn-game-secondary"}
                        disabled={isActiveEnvironment}
                        onClick={() => selectEnvironment(environment.key)}
                      >
                        {isActiveEnvironment ? "Current" : "Use"}
                      </button>
                      {!environment.readonly ? (
                        <button
                          type="button"
                          className="btn-game-secondary"
                          onClick={() => startEnvironmentEdit(environment)}
                        >
                          Edit
                        </button>
                      ) : null}
                      {!environment.readonly ? (
                        <button
                          type="button"
                          className="btn-game-danger"
                          disabled={isActiveEnvironment}
                          onClick={() => removeEnvironment(environment)}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
